import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { CommandType, UsedCommandType } from '~/constants/commands'
import { commandTypeToJSON } from '~/protos/agents'

// Define a local type to avoid circular import with ~/store
interface TutorialRootState {
  tutorial: TutorialState
}

// Define step order as a constant that can be imported by other components
export const TUTORIAL_STEPS = ['repo', 'tour', 'choose-repo', 'run-a-task'] as const
export type TutorialStep = (typeof TUTORIAL_STEPS)[number]

// Version of the onboarding flow
export const CURRENT_ONBOARDING_VERSION = 3

interface UserTutorialState {
  isHelpHubOpen: boolean
  showTooltips: boolean
  completedSteps: TutorialStep[]
  completedSubTasks: string[]
  helpHubActiveTab: 'getting-started' | 'library'
  currentDoc: UsedCommandType | null
  currentTutorial: string | null
  completedTutorials: string[]
  completedExamples: UsedCommandType[]
  completedCommands: UsedCommandType[]
}

export interface TutorialState {
  _currentUserEmail: string | null
  onboardingVersion: number
  userStates: Record<string, UserTutorialState>
}

const createInitialUserState = (): UserTutorialState => ({
  isHelpHubOpen: false,
  showTooltips: false,
  completedSteps: [],
  completedSubTasks: [],
  helpHubActiveTab: 'library',
  currentDoc: null,
  currentTutorial: null,
  completedTutorials: [],
  completedExamples: [],
  completedCommands: []
})

// Create a stable reference for the initial state to prevent unnecessary re-renders
const INITIAL_USER_STATE = createInitialUserState()

const defaultState: TutorialState = {
  _currentUserEmail: null,
  onboardingVersion: CURRENT_ONBOARDING_VERSION,
  userStates: {}
}

const tutorialSlice = createSlice({
  name: 'tutorial',
  initialState: defaultState,
  reducers: {
    initializeUser: (state, action: PayloadAction<string>) => {
      const userEmail = action.payload
      state._currentUserEmail = userEmail
      state.userStates = state.userStates || {}
      if (!state.onboardingVersion || state.onboardingVersion < CURRENT_ONBOARDING_VERSION) {
        // Reset everything if version is outdated
        state.userStates = {}
        state.onboardingVersion = CURRENT_ONBOARDING_VERSION
      }

      // Initialize user state if it doesn't exist - start onboarding
      if (!state.userStates[userEmail]) {
        state.userStates[userEmail] = createInitialUserState()
        state.userStates[userEmail].helpHubActiveTab = 'getting-started'
        state.userStates[userEmail].isHelpHubOpen = true
      }
    },

    setHelpHubOpen: (state, action: PayloadAction<boolean>) => {
      if (!state._currentUserEmail) return
      state.userStates[state._currentUserEmail].isHelpHubOpen = action.payload
    },

    setHelpHubActiveTab: (state, action: PayloadAction<'getting-started' | 'library'>) => {
      if (!state._currentUserEmail) return
      state.userStates[state._currentUserEmail].helpHubActiveTab = action.payload
    },

    setCurrentDoc: (state, action: PayloadAction<UsedCommandType | null>) => {
      if (!state._currentUserEmail) return
      state.userStates[state._currentUserEmail].currentDoc = action.payload
    },

    markStepComplete: (state, action: PayloadAction<TutorialStep>) => {
      if (!state._currentUserEmail) return
      const userState = state.userStates[state._currentUserEmail]
      if (!userState.completedSteps.includes(action.payload)) {
        userState.completedSteps.push(action.payload)

        // Also mark it as a completed tutorial
        if (!userState.completedTutorials.includes(action.payload)) {
          userState.completedTutorials.push(action.payload)
        }

        // Automatically close help hub after completing the last step
        const isLastStep = action.payload === TUTORIAL_STEPS[TUTORIAL_STEPS.length - 1]
        if (isLastStep) {
          userState.isHelpHubOpen = false
        }
      }
    },

    markSubTaskComplete: (state, action: PayloadAction<string>) => {
      if (!state._currentUserEmail) return
      const userState = state.userStates[state._currentUserEmail]

      if (!userState.completedSubTasks.includes(action.payload)) {
        userState.completedSubTasks.push(action.payload)

        // Auto-complete the main task if all subtasks are done
        const allSubTasks = [
          commandTypeToJSON(CommandType.MUNIT_TEST_CREATION),
          commandTypeToJSON(CommandType.INPUT_OUTPUT_EXAMPLE_TO_DWL_GENERATION),
          commandTypeToJSON(CommandType.GENERATE_ADD_FLOW_CODE_PR)
        ]
        if (allSubTasks.every(task => userState.completedSubTasks.includes(task))) {
          const runTaskStep = 'run-a-task' as TutorialStep
          if (!userState.completedSteps.includes(runTaskStep)) {
            userState.completedSteps.push(runTaskStep)
          }
        }
      }
    },

    startTutorial: (state, action: PayloadAction<string>) => {
      if (!state._currentUserEmail) return
      const userState = state.userStates[state._currentUserEmail]
      userState.showTooltips = true
      userState.currentTutorial = action.payload
    },

    setCurrentTutorial: (state, action: PayloadAction<string | null>) => {
      if (!state._currentUserEmail) return
      const userState = state.userStates[state._currentUserEmail]
      userState.currentTutorial = action.payload
    },

    hideTour: state => {
      if (!state._currentUserEmail) return
      const userState = state.userStates[state._currentUserEmail]
      userState.showTooltips = false
      userState.currentTutorial = null
    },

    resetTutorial: state => {
      if (!state._currentUserEmail) return
      state.userStates[state._currentUserEmail] = createInitialUserState()
    },

    cleanupUserState: (state, action: PayloadAction<string>) => {
      const userId = action.payload
      delete state.userStates[userId]
      if (state._currentUserEmail === userId) {
        state._currentUserEmail = null
      }
    },

    markExampleComplete: (state, action: PayloadAction<UsedCommandType>) => {
      if (!state._currentUserEmail) return
      const userState = state.userStates[state._currentUserEmail]

      if (!userState.completedExamples.includes(action.payload)) {
        userState.completedExamples.push(action.payload)
      }
    },

    markCommandComplete: (state, action: PayloadAction<UsedCommandType>) => {
      const FLOW_CODE_PR_COMMANDS = [
        CommandType.GENERATE_ADD_FLOW_CODE_PR,
        CommandType.GENERATE_REPO_ADD_FLOW_CODE_PR
      ] as UsedCommandType[]

      const DWL_COMMANDS = [
        CommandType.GENERATE_DATAWEAVE,
        CommandType.INPUT_OUTPUT_EXAMPLE_TO_DWL_GENERATION,
        CommandType.INPUT_OUTPUT_EXAMPLE_TO_DWL_GENERATION_REPO_AWARE
      ] as UsedCommandType[]

      const DATA_CREATION_COMMANDS = [
        CommandType.DATAWEAVE_TO_DATA_CREATION,
        CommandType.DATAWEAVE_TO_DATA_CREATION_REPO_AWARE
      ] as UsedCommandType[]

      if (!state._currentUserEmail) return
      const userState = state.userStates[state._currentUserEmail]

      if (!userState.completedCommands) {
        userState.completedCommands = []
      }

      const getCommandType = (command: UsedCommandType): string => {
        switch (true) {
          case FLOW_CODE_PR_COMMANDS.includes(command):
            return 'FLOW_CODE_PR'
          case DWL_COMMANDS.includes(command):
            return 'DWL'
          case DATA_CREATION_COMMANDS.includes(command):
            return 'DATA_CREATION'
          default:
            return 'SINGLE'
        }
      }

      switch (getCommandType(action.payload)) {
        case 'FLOW_CODE_PR':
          FLOW_CODE_PR_COMMANDS.forEach(command => {
            if (!userState.completedCommands.includes(command)) {
              userState.completedCommands.push(command)
            }
          })
          break
        case 'DWL':
          DWL_COMMANDS.forEach(command => {
            if (!userState.completedCommands.includes(command)) {
              userState.completedCommands.push(command)
            }
          })
          break
        case 'DATA_CREATION':
          DATA_CREATION_COMMANDS.forEach(command => {
            if (!userState.completedCommands.includes(command)) {
              userState.completedCommands.push(command)
            }
          })
          break
        default:
          if (!userState.completedCommands.includes(action.payload)) {
            userState.completedCommands.push(action.payload)
          }
      }
    }
  }
})

export const {
  initializeUser,
  setHelpHubOpen,
  setHelpHubActiveTab,
  setCurrentDoc,
  markStepComplete,
  startTutorial,
  hideTour,
  setCurrentTutorial,
  resetTutorial,
  cleanupUserState,
  markSubTaskComplete,
  markExampleComplete,
  markCommandComplete
} = tutorialSlice.actions

// Memoized selector to prevent unnecessary re-renders
export const selectCurrentUserTutorialState = createSelector(
  [
    (state: TutorialRootState) => state.tutorial._currentUserEmail,
    (state: TutorialRootState) => state.tutorial.userStates
  ],
  (currentUserEmail: string | null, userStates: Record<string, UserTutorialState>) => {
    return currentUserEmail ? userStates[currentUserEmail] : INITIAL_USER_STATE
  }
)

export const selectIsExampleCompleted = (
  state: TutorialRootState,
  example: UsedCommandType
): boolean => {
  const userState = selectCurrentUserTutorialState(state)
  return userState.completedExamples.includes(example)
}

export const selectIsCommandCompleted = (
  state: TutorialRootState,
  command: UsedCommandType
): boolean => {
  const userState = selectCurrentUserTutorialState(state)
  return userState.completedCommands.includes(command)
}

export { defaultState as tutorialDefaultState }
export default tutorialSlice.reducer
